import { Alert, AlertTitle, Box, Button, CircularProgress, Stack } from '@mui/material'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import BidDetailSummary from '@/features/bidding/components/bidDetail/BidDetailSummary'
import BidDetailToolbar from '@/features/bidding/components/bidDetail/toolbar/BidDetailToolbar'
import type { PtuRange } from '@/features/bidding/components/bidDetail/toolbar/PtuRangeSelector'
import ZeroVolumeFilterToggle from '@/features/bidding/components/bidDetail/toolbar/ZeroVolumeFilterToggle'
import type { MarketProgram } from '@/features/bidding/constants'
import { BidType, MARKET_TIMEZONE } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { useBidEnergyViewRows } from '@/features/bidding/hooks/bidDetail/energy/useBidEnergyViewRows'
import { useBidDetailQueryParams } from '@/features/bidding/hooks/bidDetail/useBidDetailQueryParams'
import { MarketDate } from '@/features/bidding/utils/date/marketDate'
import getBidConfig from '@/features/bidding/utils/getBidConfig'

import { buildBidEnergyColumns } from './BidEnergyColumns'
import BidEnergyStepsTable from './BidEnergyStepsTable'

type BidEnergyViewProps = Readonly<{
  marketProgram: MarketProgram
}>

function computeNowToEndRange(deliveryDay: MarketDate, ptuLengthInMinutes: number): PtuRange {
  const ptus = deliveryDay.ptus({ minutes: ptuLengthInMinutes })
  const now = DateTime.now().setZone(MARKET_TIMEZONE)
  const currentIndex = ptus.findIndex((ptu) => ptu.start <= now && now < ptu.end)
  const fromIndex = currentIndex === -1 ? 0 : currentIndex

  return { fromIndex, toIndex: Math.max(fromIndex, ptus.length - 1) }
}

/**
 * Energy-bids view — composes the shared bid-detail shell with the Energy
 * data streams + row composer.
 *
 * Owns: empty / loading / error states with explicit retry, per-column
 * "unavailable" indicators when sub-queries fail, and live wall-clock
 * updates for the "NOW" badge.
 */
export default function BidEnergyView({ marketProgram }: BidEnergyViewProps) {
  const { t } = useTranslation()
  const [queryParams, setQueryParams] = useBidDetailQueryParams()

  // Default deliveryDay to today when the URL doesn't carry one.
  useEffect(() => {
    if (queryParams.deliveryDay === null) {
      setQueryParams({ deliveryDay: new MarketDate() })
    }
  }, [queryParams.deliveryDay, setQueryParams])

  const effectiveDeliveryDay = useMemo(() => queryParams.deliveryDay ?? new MarketDate(), [queryParams.deliveryDay])
  const { selectedCountry } = useBiddingContext()
  const ptuLengthInMinutes = getBidConfig(selectedCountry, BidType.ENERGY).ptuLengthInMin
  const [ptuRange, setPtuRange] = useState<PtuRange>(() =>
    computeNowToEndRange(effectiveDeliveryDay, ptuLengthInMinutes),
  )

  // Local selection state. Seeded from the URL's `activationGroupUuid`
  // query param (if set); otherwise starts empty.
  const [selectedActivationGroupUuids, setSelectedActivationGroupUuids] = useState<ReadonlyArray<string>>(() =>
    queryParams.activationGroupUuid ? [queryParams.activationGroupUuid] : [],
  )

  // Default to hiding zero-volume rows — the trading desk sees far fewer,
  // relevant rows. Turning it off reveals them greyed out (cancelled bids etc).
  const [hideZeroVolumeRows, setHideZeroVolumeRows] = useState(true)

  const { ptus, availableActivationGroups, isLoading, isFetching, isBidError, isObligationsError, refresh } =
    useBidEnergyViewRows({
      marketProgram,
      portfolioCode: queryParams.portfolioCode,
      deliveryDay: queryParams.deliveryDay,
      selectedActivationGroupUuids,
      ptuRange,
      hideZeroVolumeRows,
    })

  useEffect(() => {
    setSelectedActivationGroupUuids((prev) => {
      if (availableActivationGroups.length === 0) {
        return prev
      }

      const availableUuids = new Set(availableActivationGroups.map((ag) => ag.uuid))
      const stillValid = prev.filter((uuid) => availableUuids.has(uuid))

      // Keep the same array reference when nothing changed: returning a new
      // (equal) array would change identity downstream, re-trigger this effect, and loop.
      if (stillValid.length === prev.length && stillValid.every((uuid, i) => uuid === prev[i])) {
        return prev
      }

      return stillValid
    })
  }, [availableActivationGroups])

  const handleResetToNow = () => {
    const today = new MarketDate()
    setQueryParams({ deliveryDay: today })
    setPtuRange(computeNowToEndRange(today, ptuLengthInMinutes))
  }

  const columns = useMemo(() => buildBidEnergyColumns(t), [t])

  const emptyMessageKey = resolveEmptyMessageKey({
    portfolioCode: queryParams.portfolioCode,
    deliveryDay: queryParams.deliveryDay,
    availableActivationGroupCount: availableActivationGroups.length,
    selectedActivationGroupCount: selectedActivationGroupUuids.length,
    isLoading,
    isBidError,
  })

  const emptyMessage = emptyMessageKey ? String(t(emptyMessageKey)) : ''

  return (
    <Box sx={{ py: 3 }}>
      <BidDetailToolbar
        activationGroupOptions={availableActivationGroups}
        bidType={BidType.ENERGY}
        extraFilters={
          <ZeroVolumeFilterToggle hideZeroVolumeRows={hideZeroVolumeRows} onChange={setHideZeroVolumeRows} />
        }
        isRefreshing={isFetching}
        marketProgram={marketProgram}
        ptuRange={ptuRange}
        queryParams={queryParams}
        selectedActivationGroupUuids={selectedActivationGroupUuids}
        onActivationGroupSelectionChange={setSelectedActivationGroupUuids}
        onBidDateChange={(deliveryDay) => setQueryParams({ deliveryDay })}
        onPtuRangeChange={setPtuRange}
        onRefresh={refresh}
        onResetToNow={handleResetToNow}
      />

      <Stack spacing={2} sx={{ mt: 2 }}>
        {isBidError && (
          <Alert
            action={
              <Button color="inherit" size="small" onClick={refresh}>
                {t('bidding.results.retry')}
              </Button>
            }
            severity="error"
          >
            <AlertTitle>{t('bidding.results.bid_fetch_error_title')}</AlertTitle>
            {t('bidding.results.bid_fetch_error_hint')}
          </Alert>
        )}

        {isObligationsError && (
          <Alert severity="warning" variant="outlined">
            {t('bidding.results.obligations_unavailable')}
          </Alert>
        )}
      </Stack>

      <BidDetailSummary
        items={[
          { label: t('bidding.results.portfolio'), value: queryParams.portfolioCode ?? '—' },
          { label: t('bidding.results.market_program'), value: t(`bidding.market_program.${marketProgram}`) },
          { label: t('bidding.results.bid_date'), value: effectiveDeliveryDay.getStartOfDay().toFormat('dd MMM yyyy') },
        ]}
      />

      <Box sx={{ mt: 3 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <BidEnergyStepsTable
            columnGroups={columns.columnGroups}
            dataColumns={columns.dataColumns}
            emptyMessage={emptyMessage}
            obligationsAvailable={!isObligationsError}
            ptuLengthInMinutes={ptuLengthInMinutes}
            ptus={ptus}
          />
        )}
      </Box>
    </Box>
  )
}

type EmptyMessageInputs = Readonly<{
  portfolioCode: string | null
  deliveryDay: MarketDate | null
  availableActivationGroupCount: number
  selectedActivationGroupCount: number
  isLoading: boolean
  isBidError: boolean
}>

type EmptyMessageKey =
  | 'bidding.results.empty.no_context'
  | 'bidding.results.empty.no_bids_for_context'
  | 'bidding.results.empty.no_ags_selected'
  | 'bidding.results.empty.no_rows_in_range'

function resolveEmptyMessageKey(inputs: EmptyMessageInputs): EmptyMessageKey | null {
  if (inputs.isLoading || inputs.isBidError) {
    // Loading is handled by the spinner; errors by the Alert. Don't show an
    // empty-state message inside the table — the caller wraps it.
    return null
  }
  if (inputs.portfolioCode === null || inputs.deliveryDay === null) {
    return 'bidding.results.empty.no_context'
  }
  if (inputs.availableActivationGroupCount === 0) {
    return 'bidding.results.empty.no_bids_for_context'
  }
  if (inputs.selectedActivationGroupCount === 0) {
    return 'bidding.results.empty.no_ags_selected'
  }

  return 'bidding.results.empty.no_rows_in_range'
}
